/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import MsDyn365, { IComponentProps, ICoreContext, IImageSettings, Image } from '@msdyn365-commerce/core';
import { ChannelDeliveryOptionConfiguration, format, ProductDeliveryOptions, SalesLine, SalesOrder, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getFallbackImageUrl, getProductPageUrlSync, ObjectExtensions, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Button, isMobile, VariantType } from '@msdyn365-commerce-modules/utilities';
import React, { useState } from 'react';

import { getDeliveryType, getDeliveryTypeString, getPickupModeDesc, IDeliveryTypeStringMap, ISalesStatusStringMap } from '../../../../../common/get-sales-line';
import { defaultImageSettings, getSalesStatusCode } from '../../../../../common/utilities';

/**
 * Data required to render each order details row.
 */
export interface IOrderDetailsTableRowData {
    salesOrder: SalesOrder;
    salesLine: SalesLine;
    product: SimpleProduct;
    deliveryOptions: ProductDeliveryOptions[];
    channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;
}

/**
 * Resources for order details table rows.
 */
export interface IOrderDetailsTableRowResources {
    orderDetailsProductDimensionsSeparator: string;
    orderDetailsQuantityMobileText: string;
    orderDetailsViewDetailsButtonText: string;
    orderDetailsViewDetailsButtonAriaLabel: string;
    pickedUpSalesLines: string;
    deliveredSalesLines: string;
    carryOutSalesLines: string;
    emailSalesLines: string;
    orderStatusReadyForPickup: string;
    orderStatusProcessing: string;
    orderStatusShipped: string;
    orderStatusPickedUp: string;
    orderStatusCanceled: string;
    orderStatusEmailed: string;
}

/**
 * Configuration for the order details rows.
 */
export interface IOrderDetailsTableRowProps extends IComponentProps<IOrderDetailsTableRowData> {
    context: ICoreContext;
    className: string;

    imageSettings?: IImageSettings;
    isRetailMultiplePickUpOptionEnabled?: boolean;

    resources: IOrderDetailsTableRowResources;
}

/**
 * Formats amount.
 * @param context - Core context.
 * @param amount - Amount to display.
 * @param currencyCode - Currency for the amount.
 * @returns String value.
 */
const formatAmount = (context: ICoreContext, amount: number | undefined, currencyCode: string | undefined) => {
    if (amount === undefined) {
        return '';
    }
    return context.cultureFormatter.formatCurrency(amount, currencyCode);
};

/**
 * Renders rows for desktop view.
 * @param props - Configuration for the rows.
 * @returns A react node with a list of order details rows for desktop view.
 */
export const OrderDetailsDesktopTableRowCellComponent: React.FC<IOrderDetailsTableRowProps> = (props: IOrderDetailsTableRowProps) => {
    const { data: { product, salesLine, salesOrder, deliveryOptions } } = props;

    const fallbackImageUrl = getFallbackImageUrl(product.ItemId, props.context.actionContext.requestContext.apiSettings);

    const pickupMode = getPickupModeDesc(product, salesLine.DeliveryMode, deliveryOptions, props.isRetailMultiplePickUpOptionEnabled);
    const pickupDeliveryModeCode = props.context.request.channel?.PickupDeliveryModeCode;
    const emailDeliveryModeCode = props.context.request.channel?.EmailDeliveryModeCode;
    const deliveryType = getDeliveryType(
        pickupMode, salesLine, salesLine.DeliveryMode, pickupDeliveryModeCode,
        props.isRetailMultiplePickUpOptionEnabled, props.data.channelDeliveryOptionConfig, emailDeliveryModeCode);
    const deliveryTypeStringMap: IDeliveryTypeStringMap = {
        pickUp: props.resources.pickedUpSalesLines,
        ship: props.resources.deliveredSalesLines,
        carryOut: props.resources.carryOutSalesLines,
        email: props.resources.emailSalesLines
    };
    const deliveryTypeString = getDeliveryTypeString(deliveryType, deliveryTypeStringMap, props.isRetailMultiplePickUpOptionEnabled);

    const salesStatusStringMap: ISalesStatusStringMap = {
        canceled: props.resources.orderStatusCanceled,
        readyForPickup: props.resources.orderStatusReadyForPickup,
        pickedUp: props.resources.orderStatusPickedUp,
        processing: props.resources.orderStatusProcessing,
        shipped: props.resources.orderStatusShipped,
        unknown: undefined,
        emailed: props.resources.orderStatusEmailed
    };

    const salesStatus = getSalesStatusCode(deliveryType, salesLine.SalesStatusValue, pickupMode);
    // eslint-disable-next-line security/detect-object-injection -- Existing code.
    const salesStatusString = salesStatusStringMap[salesStatus];

    const columnWithNumberClassName = `${props.className}__column-with-number`;

    let productUrl = '';
    if (!ObjectExtensions.isNullOrUndefined(product.Name) && !ObjectExtensions.isNullOrUndefined(product.RecordId)) {
        productUrl = getProductPageUrlSync(product.Name, product.RecordId, props.context.actionContext);
    }

    return (
        <>
            <td>
                <div className={`${props.className}__product-image-container`}>
                    <Image
                        requestContext={props.context.actionContext.requestContext}
                        className={`${props.className}__product-image`}
                        altText={product.Name}
                        src={product.PrimaryImageUrl ?? ''}
                        fallBackSrc={fallbackImageUrl}
                        gridSettings={props.context.request.gridSettings!}
                        imageSettings={props.imageSettings ?? defaultImageSettings}
                        loadFailureBehavior='empty'
                    />
                </div>
            </td>
            <td>
                {props.data.product.RecordId}
            </td>
            <td>
                <div className={`${props.className}__product-info`}>
                    <a className={`${props.className}__product-name`} href={productUrl}>
                        {product.Name}
                    </a>
                    <span className={`${props.className}__product-dimensions`}>
                        {product.Dimensions?.map(
                            dimension => dimension.DimensionValue?.Value).filter(
                            dimensionValue => !StringExtensions.isNullOrWhitespace(dimensionValue)).join(
                            props.resources.orderDetailsProductDimensionsSeparator)}
                    </span>
                </div>
            </td>
            <td className={columnWithNumberClassName}>
                {formatAmount(props.context, product.Price, salesOrder.CurrencyCode)}
            </td>
            <td>
                {deliveryTypeString}
            </td>
            <td>
                {salesStatusString}
            </td>
            <td className={columnWithNumberClassName}>
                {salesLine.Quantity}
            </td>
            <td>
                {salesLine.UnitOfMeasureSymbol}
            </td>
            <td className={columnWithNumberClassName}>
                {formatAmount(props.context, salesLine.TotalAmount, salesOrder.CurrencyCode)}
            </td>
        </>
    );
};

let setShowPreviousActions: React.Dispatch<React.SetStateAction<boolean>> | undefined;

/**
 * Renders actions popup for the mobile view.
 * @param props - Configuration for popup.
 * @returns A react node with actions..
 */
const OrderDetailsExtraActionsPopupComponent: React.FC<IOrderDetailsTableRowProps> = (props: IOrderDetailsTableRowProps): JSX.Element | null => {
    const { resources: { orderDetailsViewDetailsButtonAriaLabel, orderDetailsViewDetailsButtonText } } = props;

    const [shouldShowActions, setShowActions] = useState(false);

    const product = props.data.product;
    let productUrl = '';
    if (!StringExtensions.isNullOrWhitespace(product.Name) && !ObjectExtensions.isNullOrUndefined(product.RecordId)) {
        productUrl = getProductPageUrlSync(product.Name!, product.RecordId, props.context.actionContext);
    }

    const onClickViewDetails = React.useCallback(() => {
        if (MsDyn365.isBrowser) {
            window.location.href = productUrl;
        }
    }, [productUrl]);

    const toggle = React.useCallback(() => {
        const shouldShowActionsNewValue = !shouldShowActions;
        if (shouldShowActionsNewValue) {
            if (setShowPreviousActions) {
                setShowPreviousActions(false);
            }
            setShowActions(shouldShowActionsNewValue);
            setShowPreviousActions = setShowActions;
        } else {
            setShowPreviousActions = undefined;
            setShowActions(shouldShowActionsNewValue);
        }
    }, [shouldShowActions, setShowActions]);

    if (StringExtensions.isNullOrWhitespace(productUrl)) {
        return null;
    }

    const className = `${props.className}__extra-actions-cell`;
    const actionsContainerClassName = `${className}__actions-container`;

    return (
        <div className={className}>
            <Button className={`${className}__toggle`} onClick={toggle} />
            {
                shouldShowActions &&
                <div className={actionsContainerClassName}>
                    <Button
                        className={`${actionsContainerClassName}__view-details`}
                        onClick={onClickViewDetails}
                        aria-label={orderDetailsViewDetailsButtonAriaLabel}
                    >
                        {orderDetailsViewDetailsButtonText}
                    </Button>
                </div>
            }
        </div>
    );
};

/**
 * Renders rows for mobile view.
 * @param props - Configuration for the rows.
 * @returns A react node with a list of order details rows for mobile view.
 */
export const OrderDetailsMobileTableRowCellComponent: React.FC<IOrderDetailsTableRowProps> = (props: IOrderDetailsTableRowProps) => {
    const { data: { product, salesLine } } = props;
    const fallbackImageUrl = getFallbackImageUrl(product.ItemId, props.context.actionContext.requestContext.apiSettings);

    return (
        <>
            <td>
                <div className={`${props.className}__product-image-container`}>
                    <Image
                        requestContext={props.context.actionContext.requestContext}
                        className={`${props.className}__product-image-container__product-image`}
                        altText={product.Name}
                        src={product.PrimaryImageUrl ?? ''}
                        fallBackSrc={fallbackImageUrl}
                        gridSettings={props.context.request.gridSettings!}
                        imageSettings={props.imageSettings ?? defaultImageSettings}
                        loadFailureBehavior='empty'
                    />
                </div>
            </td>
            <td>
                <div className={`${props.className}__product-info`}>
                    <span className={`${props.className}__product-name`}>
                        {product.Name}
                    </span>
                    <span className={`${props.className}__product-quantity`}>
                        {format(props.resources.orderDetailsQuantityMobileText, salesLine.Quantity)}
                    </span>
                </div>
            </td>
            <td>
                {OrderDetailsExtraActionsPopupComponent(props)}
            </td>
        </>
    );
};

/**
 * Renders order details rows.
 * @param props - Configuration for the rows.
 * @returns A react node with a list of order details rows.
 */
export const OrderDetailsTableRowComponent: React.FC<IOrderDetailsTableRowProps> = (props: IOrderDetailsTableRowProps) => {

    const isMobileView = isMobile({ variant: VariantType.Viewport, context: props.context.request }) === 'xs';

    return (
        <tr className={props.className}>
            {
                isMobileView ? (
                    <OrderDetailsMobileTableRowCellComponent
                        {...props}
                    />
                ) : (
                    <OrderDetailsDesktopTableRowCellComponent
                        {...props}
                    />
                )
            }
        </tr>
    );
};
